// Home page copy, word for word from the DIGU Site Reference artifact.
// Rich items are { lead, text }: rendered as <b>lead</b>text, so `text` carries its own leading space.
import { ROUTES } from './routes';

export const HOME = {
  hero: {
    eyebrow: 'Security for AI adoption',
    title: 'Your engineers are already using Claude. Your security team has not signed off.',
    lede: 'We assess how safely your teams are using it, implement the controls your security team needs, and hand you the evidence your auditors ask for. All within weeks, at a fixed price, in your environment.',
    agents:
      'We start with Claude. The same controls cover GitHub Copilot, Cursor and the other coding agents your engineers use.',
    actions: [
      { label: 'Score yourself in ten questions', to: ROUTES.score },
      { label: 'See the five engagements', to: ROUTES.services, variant: 'ghost' },
    ],
    // Illustration beside the hero copy (SecTerminal). Not a real customer session: it acts
    // out the lede (assess, implement the controls, hand over the evidence) using the two
    // incident patterns further down the page and the baseline's domains (D1 identity,
    // D2 permissions, D3 untrusted content).
    terminal: {
      label:
        'Illustration: a DIGU assessment finds an unscoped API token and a CI workflow that trusts issue text, applies the controls, and exports the evidence pack for your security team.',
      title: 'digu-assess: production',
      status: { running: 'assessing', done: 'ready for sign-off' },
      lines: [
        { kind: 'cmd', text: 'digu assess --agents claude-code,copilot,cursor' },
        { kind: 'note', text: 'loading DIGU AI Security Baseline: 32 controls, 5 domains' },
        { kind: 'note', text: 'mapping agent identities, tokens and CI workflows' },
        { kind: 'fail', text: 'D1 unscoped API token found in deploy/.env' },
        { kind: 'fail', text: 'D3 CI workflow trusts text from GitHub issues' },
        { kind: 'ok', text: 'D2 approval step on production changes' },
        { kind: 'cmd', text: 'digu apply --controls D1,D3' },
        { kind: 'ok', text: 'token scoped to one service and rotated' },
        { kind: 'ok', text: 'issue text isolated from CI credentials' },
        { kind: 'ok', text: 'evidence pack exported for your auditors' },
      ],
      summary: { label: 'controls in place', from: 23, to: 31, of: 32 },
    },
    // No Anthropic / Claude Partner Network logo until Anthropic approves its use (website
    // feedback). The partnership is a text item like "Okta partner" for now.
    trust: {
      items: [
        'Certified Claude Services Partner',
        'Okta partner',
        'Mapped to OWASP, NIST AI RMF and ISO/IEC 42001',
        'Certified Claude engineers on every project',
      ],
    },
  },

  incidents: {
    eyebrow: 'Two incidents, 2026',
    title: 'Neither of these was a model failure',
    // `source` is the URL shown as a "Source" link under each incident (hidden when null).
    sourceLabel: 'Source',
    items: [
      {
        when: 'April 2026',
        text: "An agent running on Claude inside another vendor's tool deleted a company's production database and its backups in nine seconds. It had found an unscoped API token sitting in an unrelated file.",
        source:
          'https://ia.acs.org.au/article/2026/gone-in-9-seconds--ai-agent-deletes-company-database.html',
      },
      {
        when: 'August 2026',
        text: 'Researchers at Black Hat showed that three major coding agents, Claude Code Action among them, could be tricked by text written inside a GitHub issue into leaking CI credentials. The workflows were on default settings.',
        source:
          'https://thehackernews.com/2026/08/claude-code-and-gemini-cli-flaws-let.html',
      },
    ],
    closing:
      'Both were configuration. A token with too much reach. A workflow that trusted outside input. An approval step that did not exist. All three are things you can fix this quarter.',
  },

  problem: {
    eyebrow: 'The problem',
    title: 'Buying the seats was the easy part',
    items: [
      {
        title: 'Nobody knows if it is working.',
        text: 'Most companies cannot say how many of the Claude seats they pay for were used last week, or whether anything shipped faster because of them.',
      },
      {
        title: 'Security has not said yes.',
        text: 'The controls exist. Turning them on is a project, and nobody on your platform team has a spare month for it.',
      },
      {
        title: 'There is no evidence.',
        text: 'Ask whether an action in your logs came from an agent or a person. Most teams cannot answer, and neither can their auditors.',
      },
    ],
  },

  howItRuns: {
    eyebrow: 'How it runs',
    title: 'Four steps, in order',
    steps: [
      {
        k: 'Step 1',
        title: 'Assess',
        text: 'One week. We score you against 32 controls using your own data, and give you a costed plan.',
      },
      {
        k: 'Step 2',
        title: 'Contain',
        text: 'Three to four weeks. We implement the controls, and your security team signs off.',
      },
      {
        k: 'Step 3',
        title: 'Govern',
        text: 'Standards, approvals and monitoring in place, and your developers actually adopt the tools.',
      },
      {
        k: 'Step 4',
        title: 'Scale',
        text: 'Engineers create, copy and tear down cloud environments through Claude, safely.',
      },
    ],
  },

  // Built but not rendered yet: switch it on in SecurityHome.vue once the video
  // and the own-score page are real.
  proof: {
    eyebrow: 'Proof',
    title: 'Check the work before you call us',
    items: [
      {
        lead: 'Watch a destructive action get blocked twice.',
        text: ' Three minutes. Once by cloud policy, once by identity. No slides.',
      },
      {
        lead: 'Read our own control register.',
        text: ' We scored DIGU against the same thirty two controls we score you against, and published the result.',
      },
      {
        lead: 'Read a sample assessment report.',
        text: ' Sanitized, but the real shape of what you get at the end of week one.',
      },
      {
        lead: 'See the controls on a live product.',
        text: ' We score Storemandu, the storefront platform we build and run, against the same thirty two controls. Real customers, real payments, apprentices working in it.',
      },
    ],
  },

  whoWeWorkWith: {
    eyebrow: 'Who we work with',
    title: 'We work best where the evidence matters',
    intro:
      'Our maturity model puts most mid-size software companies at level 2 as a twelve month target. Regulated organizations belong at level 3 across all five domains, because their auditors will ask and the evidence pack is the answer. That is the work we are built for.',
    items: [
      { lead: 'Healthcare.', text: ' Your developers want Claude. Your auditors want evidence.' },
      { lead: 'Financial services.', text: ' Your engineers want Claude. Your examiners want evidence.' },
    ],
    also: 'We also work with mid-size software companies answering customer security reviews, typically 50 to 300 developers on AWS or Azure, already paying for Claude.',
    actions: [{ label: 'Score yourself in ten questions', to: ROUTES.score }],
  },
};
